/**
 * A5 · প্রোফাইল — `/onboarding/profile`
 * Frame: `Oprappo P0 Screens.dc.html`, "A5 · প্রোফাইল".
 *
 * Frame note: "The area field says what it buys the user. A field that explains its own
 * benefit gets filled far more often than one that just sits there."
 *
 * Both fields are drawn `height:56px` with the label `500 14px/1.75` at `margin-bottom:7px`.
 * `Input` in @oprappo/ui is § 02's `h-[52px]` with a `mb-[6px]` label, so it does not fit
 * this frame; transcribed here and the difference reported. Same for the area row, which
 * is a `Select`-shaped control at 56px rather than § 02's.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════
 * WIRED — AND THE WIRING IS THE FIX.
 * ═══════════════════════════════════════════════════════════════════════════════════
 * সংরক্ষণ করুন means "save it", and until this call existed it saved nothing. The field
 * was a `useState` and the button was `navigate('/')`: the customer typed their name,
 * pressed a button that said the name was being kept, and arrived at a home screen that
 * greeted nobody. The screen was not broken in any way a screenshot could show — which
 * is why it survived a pixel gate at 2% for as long as it did.
 *
 * `PATCH /v1/account/profile` with `{ name_bn }` is the route that already existed for
 * it (`apps/api/src/account/index.ts § F1/F2/F5`), and it answers with the whole account
 * so F1's header and F2's field cannot end up disagreeing about the name. That is why the
 * name survives a reload: it is in `identities.display_name`, and every later read of
 * `GET /v1/account/profile` returns it.
 *
 * ── WHAT HAPPENS WHEN THE SAVE FAILS, AND WHY IT IS NOT THE SAME ANSWER TWICE ──
 * ইন্টারনেট নেই is drawn, and the matrix admits it on onboarding, so a request that never
 * left renders that frame with its own retry and the typed name is still in the field
 * behind it. Nothing else is drawn here. For every other refusal the screen records the
 * failure (`auth/failure.tsx`) and CONTINUES to the home screen, because the frame says
 * "দুটোই ঐচ্ছিক। পরেও দিতে পারবেন।" — F2 can set the name later, and trapping a new
 * customer on the last step of onboarding over an optional field is a worse product than
 * losing the field. The failure is loud in the operator's log and silent on the screen,
 * which is the same rule `states/unmapped.ts` states at length.
 *
 * ── FIDELITY: NOTHING BELOW MOVED ──────────────────────────────────────────────
 * No control gained a disabled style, a spinner or a changed label; the double-submit
 * guard is a flag the handler reads and the render never does. A5's healthy frame is byte
 * for byte what it was.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton, Icon } from '@oprappo/ui';
import { Frame, SAND_FRAME } from './frame.js';
import { saveProfile } from '../account/api.js';
import { noteFailure, renderAuthFailure } from '../../auth/failure.js';

/**
 * Send the typed name to the account, or say there was nothing to send.
 *
 * A FUNCTION AND NOT A CLICK HANDLER, so the one thing that matters here — the exact
 * bytes that leave the browser — is assertable without a DOM. This tree's suites run on
 * `environment: 'node'` and stub `globalThis.fetch`; a save buried in a JSX closure could
 * only be checked by reading the source, and reading the source is how the screen came to
 * have a button that said সংরক্ষণ করুন and saved nothing.
 *
 * An EMPTY field is not a write, and `'empty'` says so rather than throwing. `ProfileBody`
 * has `name_bn: nonEmpty.optional()`, so sending `''` is a 422 the customer caused by
 * leaving an optional field alone — and `''` is not a name anybody wants written over the
 * one they may already have.
 */
export async function submitProfileName(raw: string): Promise<'saved' | 'empty'> {
  const name_bn = raw.trim();
  if (name_bn === '') return 'empty';
  await saveProfile({ name_bn });
  return 'saved';
}

export function ProfileScreen(): JSX.Element {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const navigate = useNavigate();

  /** Keep the name, then go home. The write itself is `submitProfileName` above. */
  async function save(): Promise<void> {
    if (saving) return;
    setSaving(true);
    try {
      await submitProfileName(name);
      setFailure(null);
      navigate('/');
    } catch (cause) {
      setFailure(noteFailure('A5 প্রোফাইল · PATCH /v1/account/profile', cause));
      // A drawn failure holds the screen so the customer can retry; anything else has no
      // frame, and the header above says why that means going on rather than stopping.
      if (renderAuthFailure(cause) === null) navigate('/');
    } finally {
      setSaving(false);
    }
  }

  const drawn = renderAuthFailure(failure, {
    onRetry: () => {
      setFailure(null);
      void save();
    },
    onBack: () => {
      setFailure(null);
    },
  });
  if (drawn !== null) return drawn;

  return (
    <Frame className={`${SAND_FRAME} px-[24px] pt-[20px] pb-[24px]`}>
      <div className="flex justify-between items-center">
        <IconButton control="back" size={22} onClick={() => navigate(-1)} />
        <button
          type="button"
          onClick={() => navigate('/')}
          className="font-anek font-medium text-[15px] leading-[1.75] text-link bg-transparent border-0 p-0 rounded-none"
        >
          এড়িয়ে যান
        </button>
      </div>

      <div className="mt-[28px]">
        <h1 className="m-0 font-anek font-semibold text-[24px] leading-[1.25] text-ink">
          আপনাকে কী নামে ডাকব?
        </h1>
        <div className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[7px]">
          দুটোই ঐচ্ছিক। পরেও দিতে পারবেন।
        </div>
      </div>

      <div className="mt-[26px] flex flex-col gap-[18px]">
        <div>
          <label
            htmlFor="a5-name"
            className="block font-anek font-medium text-[14px] leading-[1.75] text-ink2 mb-[7px]"
          >
            নাম
          </label>
          <input
            id="a5-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="যেমন — রফিকুল ইসলাম"
            className="w-full border border-border bg-white h-[56px] px-[15px] font-anek font-normal text-[16px] leading-[1.75] text-ink placeholder:text-muted outline-none rounded-none"
          />
        </div>

        <div>
          <div className="font-anek font-medium text-[14px] leading-[1.75] text-ink2 mb-[7px]">
            এলাকা
          </div>
          <button
            type="button"
            className="w-full border border-border bg-white h-[56px] flex items-center justify-between px-[15px] rounded-none"
          >
            <span className="font-anek font-normal text-[16px] leading-[1.75] text-ink">
              মিরপুর ১০, ঢাকা
            </span>
            <Icon name="chevronDown" size={17} stroke="#5B6B69" />
          </button>
          <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[7px]">
            এলাকা দিলে কাছাকাছি সেবা আগে দেখানো যায়।
          </div>
        </div>
      </div>

      <div className="flex-1" />

      <Button tone="primary" size="lg" onClick={() => void save()}>
        সংরক্ষণ করুন
      </Button>
    </Frame>
  );
}
